import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Edit3, Trash2, Save, X, ExternalLink, Megaphone, Search, MessageCircle, Copy, Check, Eye, Inbox, CopyPlus } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import ImageUploader from './ImageUploader';
import AdminSearch from './AdminSearch';
import SEOSettingsBlock from './SEOSettingsBlock';

const EMPTY = {
  slug: '',
  title: '',
  subtitle: '',
  description: '',
  hero_image: '',
  package_codes: [],
  seo_title: '',
  seo_description: '',
  seo_og_image: '',
  published: true,
};

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const LandingPagesManager = () => {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState({});
  const [archivedCount, setArchivedCount] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slug being edited, 'new' or null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [pkgQuery, setPkgQuery] = useState('');
  const [copied, setCopied] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const [lpRes, pkgRes, statsRes, archRes] = await Promise.all([
        axios.get(`${API}/admin/landing-pages`, { params: { include_archived: showArchived }, headers: adminAuthHeader() }),
        axios.get(`${API}/admin/packages`, { headers: adminAuthHeader() }),
        axios.get(`${API}/admin/landing-pages/stats`, { headers: adminAuthHeader() }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/landing-pages/archived-count`, { headers: adminAuthHeader() }).catch(() => ({ data: { archived: 0 } })),
      ]);
      setItems(lpRes.data || []);
      setPackages(pkgRes.data || []);
      setStats(statsRes.data || {});
      setArchivedCount(archRes.data?.archived || 0);
    } catch (_) { /* ignore */ } finally { setLoading(false); }
  };

  const startNew = () => { setForm({ ...EMPTY }); setEditing('new'); setMsg(''); };
  const startEdit = (lp) => {
    setForm({
      slug: lp.slug || '',
      title: lp.title || '',
      subtitle: lp.subtitle || '',
      description: lp.description || '',
      hero_image: lp.hero_image || '',
      package_codes: lp.package_codes || [],
      seo_title: lp.seo_title || '',
      seo_description: lp.seo_description || '',
      seo_og_image: lp.seo_og_image || '',
      published: lp.published !== false,
    });
    setEditing(lp.slug);
    setMsg('');
  };
  const cancel = () => { setEditing(null); setMsg(''); };

  const save = async () => {
    if (!form.title || !form.slug) { setMsg('Slug and title are required'); return; }
    setSaving(true); setMsg('');
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/landing-pages`, form, { headers: adminAuthHeader() });
      } else {
        await axios.put(`${API}/admin/landing-pages/${editing}`, form, { headers: adminAuthHeader() });
      }
      setMsg('Saved!');
      await load();
      setTimeout(() => { setEditing(null); setMsg(''); }, 700);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete landing page "/p/${slug}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/landing-pages/${slug}`, { headers: adminAuthHeader() });
      await load();
    } catch (_) { /* ignore */ }
  };

  const duplicate = async (slug) => {
    try {
      const res = await axios.post(`${API}/admin/landing-pages/${slug}/duplicate`, {}, { headers: adminAuthHeader() });
      await load();
      // Jump straight into editing the clone so the user can rename & publish.
      if (res.data?.slug) startEdit(res.data);
    } catch (_) { window.alert('Could not duplicate. Please try again.'); }
  };

  const togglePackage = (code) => {
    setForm((f) => {
      const has = f.package_codes.includes(code);
      return { ...f, package_codes: has ? f.package_codes.filter((c) => c !== code) : [...f.package_codes, code] };
    });
  };

  const movePackage = (idx, dir) => {
    setForm((f) => {
      const arr = [...f.package_codes];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, package_codes: arr };
    });
  };

  const filteredPackages = useMemo(() => {
    const q = pkgQuery.trim().toLowerCase();
    if (!q) return packages;
    return packages.filter((p) => (p.title + ' ' + p.pkg_code + ' ' + p.destination).toLowerCase().includes(q));
  }, [packages, pkgQuery]);

  const selectedPackages = form.package_codes
    .map((c) => packages.find((p) => p.pkg_code === c))
    .filter(Boolean);

  const copyUrl = (slug) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/p/${slug}`;
    navigator.clipboard?.writeText(url);
    setCopied(slug);
    setTimeout(() => setCopied(''), 1200);
  };

  const shareWA = (lp) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/api/share/landing/${lp.slug}`;
    const text = `*${lp.title}*\n${lp.subtitle || ''}\n\nCheck it out: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  // ---------- Editor view ----------
  if (editing) {
    return (
      <div className="space-y-5" data-testid="landing-page-editor">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{editing === 'new' ? 'New Landing Page' : `Editing /p/${editing}`}</h2>
            <p className="text-sm text-gray-500">Marketing campaign page with a hero, description and linked packages.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={cancel} className="text-sm text-gray-700 border border-gray-200 rounded-md px-4 py-2 hover:bg-gray-50" data-testid="landing-cancel-btn">
              <X size={13} className="inline mr-1" /> Cancel
            </button>
            <button onClick={save} disabled={saving} className="text-sm bg-[#0d64a8] hover:bg-[#0a548e] text-white rounded-md px-5 py-2 disabled:opacity-70" data-testid="landing-save-btn">
              <Save size={13} className="inline mr-1" /> {saving ? 'Saving...' : 'Save Landing Page'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left col: content */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-2">Basics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                  <input
                    value={form.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setForm((f) => ({ ...f, title, slug: (editing === 'new' && !f.slug) ? slugify(title) : f.slug }));
                    }}
                    placeholder="e.g. Maldives Overwater Escapes"
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"
                    data-testid="landing-title-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">URL Slug *</label>
                  <div className="flex items-stretch">
                    <span className="inline-flex items-center px-2.5 border border-r-0 border-gray-200 rounded-l-md bg-gray-50 text-xs text-gray-500">/p/</span>
                    <input
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                      placeholder="maldives-deals"
                      disabled={editing !== 'new'}
                      className="flex-1 border border-gray-200 rounded-r-md px-3 py-2 text-sm focus:border-[#0d64a8] disabled:bg-gray-50 disabled:text-gray-500"
                      data-testid="landing-slug-input"
                    />
                  </div>
                  {editing !== 'new' && <p className="text-[10px] text-gray-400 mt-1">Slug can't be changed after creation (would break existing shared links).</p>}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Subtitle</label>
                <input
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  placeholder="Short one-liner shown under the title in the hero"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"
                  data-testid="landing-subtitle-input"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description / Details <span className="text-gray-400">(separate paragraphs with blank lines)</span></label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={10}
                  placeholder="Tell travellers what this campaign is about — destination highlights, what's included, why it's special."
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] leading-relaxed"
                  data-testid="landing-description-input"
                />
              </div>

              <div>
                <ImageUploader
                  value={form.hero_image}
                  onChange={(url) => setForm({ ...form, hero_image: url })}
                  label="Hero Image"
                  hint="Wide landscape (1600×900 or larger). Used as the top banner and og:image on social shares."
                  height="h-48"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={form.published ? 'yes' : 'no'}
                  onChange={(e) => setForm({ ...form, published: e.target.value === 'yes' })}
                  className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]"
                  data-testid="landing-status-select"
                >
                  <option value="yes">Published</option>
                  <option value="no">Draft (hidden)</option>
                </select>
              </div>
            </div>

            <SEOSettingsBlock
              values={{ seo_title: form.seo_title, seo_description: form.seo_description, seo_og_image: form.seo_og_image }}
              onChange={(k, v) => setForm({ ...form, [k]: v })}
              heading="SEO Settings"
              subheading="Overrides the global defaults for this landing page. Leave empty to fall back."
              testIdPrefix="landing-seo"
            />
          </div>

          {/* Right col: package picker */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3 lg:sticky lg:top-24">
              <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-2 flex items-center justify-between">
                Linked Packages
                <span className="text-[11px] font-medium bg-[#e8f2fb] text-[#0d64a8] rounded-full px-2 py-0.5">{form.package_codes.length}</span>
              </h3>

              {selectedPackages.length > 0 && (
                <div className="space-y-1.5" data-testid="landing-selected-packages">
                  {selectedPackages.map((p, idx) => (
                    <div key={p.pkg_code} className="flex items-center gap-2 bg-[#f4f8fc] border border-[#0d64a8]/15 rounded-md px-2.5 py-1.5">
                      <span className="text-[10px] font-mono bg-[#0d64a8] text-white rounded px-1.5 py-0.5 shrink-0">{idx + 1}</span>
                      <span className="text-xs text-gray-800 flex-1 truncate">{p.title}</span>
                      <button onClick={() => movePackage(idx, -1)} disabled={idx === 0} className="text-xs text-gray-500 hover:text-[#0d64a8] disabled:opacity-30">↑</button>
                      <button onClick={() => movePackage(idx, 1)} disabled={idx === selectedPackages.length - 1} className="text-xs text-gray-500 hover:text-[#0d64a8] disabled:opacity-30">↓</button>
                      <button onClick={() => togglePackage(p.pkg_code)} className="text-gray-400 hover:text-red-500"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}

              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={pkgQuery}
                  onChange={(e) => setPkgQuery(e.target.value)}
                  placeholder="Search packages..."
                  className="w-full pl-7 pr-3 py-1.5 border border-gray-200 rounded-md text-xs focus:border-[#0d64a8]"
                  data-testid="landing-package-search"
                />
              </div>

              <div className="max-h-[320px] overflow-y-auto space-y-1 pr-1" data-testid="landing-package-picker">
                {filteredPackages.map((p) => {
                  const selected = form.package_codes.includes(p.pkg_code);
                  return (
                    <button
                      key={p.pkg_code}
                      onClick={() => togglePackage(p.pkg_code)}
                      type="button"
                      data-testid={`landing-package-option-${p.pkg_code}`}
                      className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-xs border transition ${selected ? 'border-[#0d64a8] bg-[#e8f2fb]' : 'border-gray-100 hover:border-[#0d64a8]/50 hover:bg-gray-50'}`}
                    >
                      <span className={`w-3.5 h-3.5 rounded border ${selected ? 'bg-[#0d64a8] border-[#0d64a8]' : 'border-gray-300'} flex items-center justify-center shrink-0`}>
                        {selected && <Check size={9} className="text-white" />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium text-gray-800 truncate">{p.title}</span>
                        <span className="block text-[10px] text-gray-500">#{p.pkg_code} · {p.destination || p.category}</span>
                      </span>
                      <span className="text-[10px] text-gray-500 shrink-0">₹{(p.price || 0).toLocaleString('en-IN')}</span>
                    </button>
                  );
                })}
                {filteredPackages.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-4">No packages match your search.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {msg && <div className={`text-sm ${msg === 'Saved!' ? 'text-green-600' : 'text-red-600'}`}>{msg}</div>}
      </div>
    );
  }

  // ---------- List view ----------
  return (
    <div data-testid="landing-pages-manager">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone size={20} className="text-[#0d64a8]" /> Landing Pages
          </h2>
          <p className="text-sm text-gray-500 mt-1">Campaign pages served at <span className="font-mono text-gray-700">/p/&lt;slug&gt;</span>. Enquiries from these pages are tagged with the slug.</p>
        </div>
        <div className="flex items-center gap-2">
          {(archivedCount > 0 || showArchived) && (
            <button
              onClick={() => setShowArchived((v) => !v)}
              data-testid="landing-archived-toggle"
              className={`text-xs font-medium px-3 py-2 rounded-md border transition ${showArchived ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
              title="Drafts untouched for 30+ days are auto-hidden"
            >
              {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
            </button>
          )}
          <button
            onClick={startNew}
            data-testid="landing-new-btn"
            className="bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm font-medium px-4 py-2 rounded-md flex items-center gap-1.5"
          >
            <Plus size={14} /> New Landing Page
          </button>
        </div>
      </div>

      <div className="mb-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search title, slug, subtitle..." testId="landing-search" />
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500 text-sm">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <Megaphone className="mx-auto text-gray-300 mb-2" size={42} />
          <p className="text-gray-600 font-medium">No landing pages yet</p>
          <p className="text-sm text-gray-400 mt-1">Create your first campaign page to start collecting tagged enquiries.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.filter((lp) => {
            const s = q.trim().toLowerCase();
            if (!s) return true;
            return [lp.title, lp.slug, lp.subtitle, lp.seo_title].filter(Boolean).join(' ').toLowerCase().includes(s);
          }).map((lp) => {
            const s = stats[lp.slug] || { visits: 0, enquiries: 0 };
            const conv = s.visits > 0 ? Math.round((s.enquiries / s.visits) * 100) : null;
            return (
            <div key={lp.slug} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition" data-testid={`landing-card-${lp.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate">{lp.title}</h3>
                    <span className={`text-[10px] font-medium uppercase tracking-widest px-1.5 py-0.5 rounded ${lp.published !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {lp.published !== false ? 'Live' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 font-mono mt-0.5">/p/{lp.slug}</p>
                  {lp.subtitle && <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">{lp.subtitle}</p>}
                  <p className="text-[11px] text-gray-500 mt-2">{(lp.package_codes || []).length} linked package{(lp.package_codes || []).length === 1 ? '' : 's'}</p>
                </div>
              </div>

              {/* Analytics row */}
              <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`landing-stats-${lp.slug}`}>
                <div className="bg-gray-50 rounded-md px-2.5 py-1.5">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-500">
                    <Eye size={10} /> Visits
                  </div>
                  <div className="text-base font-bold text-gray-900 mt-0.5" data-testid={`landing-visits-${lp.slug}`}>{s.visits.toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-2.5 py-1.5">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-500">
                    <Inbox size={10} /> Enquiries
                  </div>
                  <div className="text-base font-bold text-[#0d64a8] mt-0.5" data-testid={`landing-enquiries-${lp.slug}`}>{s.enquiries.toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-2.5 py-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">Conv.</div>
                  <div className={`text-base font-bold mt-0.5 ${conv !== null ? 'text-green-600' : 'text-gray-400'}`}>
                    {conv !== null ? `${conv}%` : '—'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-100">
                <a href={`/p/${lp.slug}`} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]" title="Open"><ExternalLink size={13} /></a>
                <button onClick={() => copyUrl(lp.slug)} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]" title="Copy URL" data-testid={`landing-copy-${lp.slug}`}>
                  {copied === lp.slug ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                </button>
                <button onClick={() => shareWA(lp)} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-[#25D366] hover:border-[#25D366] hover:bg-[#25D366]/10" title="Share on WhatsApp">
                  <MessageCircle size={13} />
                </button>
                <button onClick={() => duplicate(lp.slug)} data-testid={`landing-duplicate-${lp.slug}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]" title="Duplicate campaign">
                  <CopyPlus size={13} />
                </button>
                <div className="flex-1" />
                <button onClick={() => startEdit(lp)} data-testid={`landing-edit-${lp.slug}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]" title="Edit"><Edit3 size={13} /></button>
                <button onClick={() => remove(lp.slug)} data-testid={`landing-delete-${lp.slug}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:border-red-400 hover:text-red-500" title="Delete"><Trash2 size={13} /></button>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
};

export default LandingPagesManager;
